// project-activity-panel-repaired-v42
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, CheckCheck, Mail, MessageSquarePlus, Plus, RefreshCw, Send, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KANBAN_REFRESH_EVENT = "inksight:kanban-refresh-needed";
const ATTENTION_ACTIVITY_TYPES = ["question", "needs_response", "approval_needed", "blocked"];
const NOTE_PREVIEW_LIMIT = 240;

const ACTIVITY_TYPE_OPTIONS = [
  { value: "fyi", label: "General Note" },
  { value: "approval_note", label: "Approval" },
  { value: "customer_update", label: "Customer Feedback" },
  { value: "internal_note", label: "Internal Update" },
  { value: "question", label: "Question" },
  { value: "needs_response", label: "Needs Response" },
  { value: "blocked", label: "Blocked" },
];

const activityLabel = (activity) => activity.activity_label
  || ACTIVITY_TYPE_OPTIONS.find((item) => item.value === activity.activity_type)?.label
  || String(activity.activity_type || "Activity").replaceAll("_", " ");

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const activityItemsFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.activities)) return payload.activities;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const activityDescription = (activity) => String(
  activity?.description
  || activity?.message
  || activity?.body_preview
  || activity?.email_body_preview
  || activity?.email_subject
  || "",
);

const isEmailActivity = (activity) => (
  activity?.activity_type === "email_reply"
  || activity?.channel === "email"
  || activity?.source === "email"
  || Boolean(activity?.email_subject)
  || Boolean(activity?.email_message_id)
  || Boolean(activity?.email_internet_message_id)
  || Boolean(activity?.email_conversation_id)
);

const activityTimeValue = (activity) => {
  const raw = activity?.email_received_at || activity?.created_at || activity?.received_at || "";
  const value = new Date(raw).getTime();
  return Number.isFinite(value) ? value : 0;
};

const sortNewestFirst = (items) => [...items].sort((a, b) => activityTimeValue(b) - activityTimeValue(a));

const queueAttentionRequested = ({ activityType, notifySales, notifyGraphics, activity }) => Boolean(
  activity?.project_attention_active
  || activity?.needs_queue_attention
  || notifySales
  || notifyGraphics
  || ATTENTION_ACTIVITY_TYPES.includes(activityType),
);

const signalKanbanRefresh = () => {
  try {
    sessionStorage.setItem("inksight-kanban-refresh-token", String(Date.now()));
  } catch {
    // Ignore storage failures; the window event still refreshes mounted queues.
  }
  window.dispatchEvent(new Event(KANBAN_REFRESH_EVENT));
};

const shouldCollapseDescription = (description) => (
  description.length > NOTE_PREVIEW_LIMIT || description.includes("\n")
);

export const ProjectActivityPanelV27 = ({
  projectId,
  activities = [],
  setActivities,
  readOnlyHistorical = false,
}) => {
  const { role, user } = useAuth();
  const [message, setMessage] = useState("");
  const [activityType, setActivityType] = useState("fyi");
  const [notifySales, setNotifySales] = useState(false);
  const [notifyGraphics, setNotifyGraphics] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [reloadingActivities, setReloadingActivities] = useState(false);
  const [expandedActivityIds, setExpandedActivityIds] = useState([]);
  const [activityView, setActivityView] = useState("all");

  const activityItems = activityItemsFromPayload(activities);
  const chronologicalActivityItems = useMemo(() => sortNewestFirst(activityItems), [activityItems]);
  const emailActivities = useMemo(() => sortNewestFirst(activityItems.filter(isEmailActivity)), [activityItems]);
  const nonEmailActivities = useMemo(() => sortNewestFirst(activityItems.filter((activity) => !isEmailActivity(activity))), [activityItems]);
  const visibleActivityItems = activityView === "emails"
    ? emailActivities
    : activityView === "system"
      ? nonEmailActivities
      : chronologicalActivityItems;

  const unreadCount = activityItems.filter((activity) => activity.is_unread).length;
  const hasProjectAttention = activityItems.some((activity) => activity.project_attention_active);
  const canAddActivity = !readOnlyHistorical && Boolean(user);

  const loadActivities = async ({ showToast = false } = {}) => {
    if (!projectId || !setActivities) return [];
    setReloadingActivities(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/activity`);
      const nextActivities = activityItemsFromPayload(response.data);
      const nextEmailCount = nextActivities.filter(isEmailActivity).length;
      setActivities(nextActivities);
      if (showToast) {
        toast.success(`Loaded ${nextActivities.length} timeline activit${nextActivities.length === 1 ? "y" : "ies"}${nextEmailCount ? `, including ${nextEmailCount} email import${nextEmailCount === 1 ? "" : "s"}` : ""}`);
      }
      return nextActivities;
    } finally {
      setReloadingActivities(false);
    }
  };

  useEffect(() => {
    loadActivities().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!composerOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !sending) setComposerOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [composerOpen, sending]);

  const refreshActivities = () => {
    loadActivities({ showToast: true }).catch((error) => {
      toast.error(error.response?.data?.detail || "Failed to refresh activity timeline");
    });
  };

  const toggleActivityExpanded = (activityId) => {
    setExpandedActivityIds((previous) => (
      previous.includes(activityId)
        ? previous.filter((id) => id !== activityId)
        : [...previous, activityId]
    ));
  };

  const addActivity = async () => {
    if (!canAddActivity) {
      toast.error("Only Graphics can add activity logs in V1");
      return;
    }

    const cleaned = message.trim();
    if (!cleaned) {
      toast.error("Activity message is required");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/activity`, {
        message: cleaned,
        activity_type: activityType,
        notify_sales: notifySales,
        notify_graphics: notifyGraphics,
      });
      setActivities(sortNewestFirst([response.data, ...activityItems]));
      if (queueAttentionRequested({ activityType, notifySales, notifyGraphics, activity: response.data })) {
        signalKanbanRefresh();
      }
      setMessage("");
      setActivityType("fyi");
      setNotifySales(false);
      setNotifyGraphics(false);
      setComposerOpen(false);
      const status = response.data?.notification_status;
      if (notifySales || notifyGraphics) {
        toast.success(status === "sent" ? "Activity added and notification sent" : "Activity added; notification not sent");
      } else {
        toast.success("Activity added");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add activity");
    } finally {
      setSending(false);
    }
  };

  const markRead = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/activity/read`);
      setActivities(activityItems.map((activity) => ({
        ...activity,
        is_unread: false,
        project_attention_active: false,
        project_attention_target: "",
      })));
      if (hasProjectAttention) signalKanbanRefresh();
      if (hasProjectAttention && unreadCount > 0) {
        toast.success("Activity marked read and queue attention cleared");
      } else if (hasProjectAttention) {
        toast.success("Queue attention cleared");
      } else {
        toast.success("Activity marked read");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear activity attention");
    }
  };

  const renderActivity = (activity) => {
    const description = activityDescription(activity);
    const emailActivity = isEmailActivity(activity);
    const isExpanded = expandedActivityIds.includes(activity.id);
    const collapsible = shouldCollapseDescription(description);
    const visibleDescription = collapsible && !isExpanded
      ? `${description.slice(0, NOTE_PREVIEW_LIMIT).trimEnd()}…`
      : description;

    return (
      <div
        key={activity.id || activity.email_message_id || activity.email_internet_message_id}
        className={`rounded-lg border p-3 ${emailActivity ? "border-cyan-500/40 bg-cyan-500/10" : activity.is_unread ? "border-primary/50 bg-primary/10" : "border-border bg-secondary/30"}`}
      >
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium">
            {activityLabel(activity)}
          </span>
          {emailActivity && (
            <span className="inline-flex items-center gap-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-200">
              <Mail size={11} /> Imported Email
            </span>
          )}
          {activity.requires_response && (
            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-200">
              Response requested
            </span>
          )}
          {activity.notification_status === "sent" && (
            <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200">
              Notified
            </span>
          )}
        </div>

        {emailActivity && activity.email_subject && (
          <p className="mb-1 text-xs font-semibold text-cyan-100">{activity.email_subject}</p>
        )}

        <p className="whitespace-pre-wrap text-sm font-medium">{visibleDescription || "No activity details."}</p>

        {collapsible && (
          <button
            type="button"
            onClick={() => toggleActivityExpanded(activity.id)}
            className="mt-2 text-xs font-medium text-primary hover:underline"
          >
            {isExpanded ? "Show less" : "Show full note"}
          </button>
        )}

        <p className="mt-2 text-xs text-muted-foreground">
          {activity.user_name || activity.email_from_name || "System"} · {formatDateTime(activity.email_received_at || activity.created_at || activity.received_at)}
        </p>

        {emailActivity && (activity.email_from || activity.email_mailbox) && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {activity.email_from && <>From: {activity.email_from}</>}
            {activity.email_from && activity.email_mailbox && " · "}
            {activity.email_mailbox && <>Mailbox: {activity.email_mailbox}</>}
          </p>
        )}

        {(activity.notified_emails || []).length > 0 && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            Notified: {(activity.notified_emails || []).join(", ")}
          </p>
        )}
      </div>
    );
  };

  return (
    <section className="bg-card border border-border rounded-xl p-6 xl:col-span-1">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquarePlus size={18} />
          <div>
            <h2 className="text-lg font-semibold">Activity Timeline</h2>
            <p className="text-xs text-muted-foreground">Project comments, notifications, and email replies.</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={refreshActivities}
            disabled={reloadingActivities}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-xs hover:bg-secondary/80 disabled:opacity-50"
            title="Reload the Activity Timeline"
          >
            <RefreshCw size={14} className={reloadingActivities ? "animate-spin" : ""} /> Refresh
          </button>
          {canAddActivity && (
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              title="Add activity"
            >
              <Plus size={14} /> Add
            </button>
          )}
          {(unreadCount > 0 || hasProjectAttention) && (
            <button
              type="button"
              onClick={markRead}
              className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-xs hover:bg-secondary/80"
              title={hasProjectAttention ? "Clear this project from the Graphics Queue attention marker" : "Mark activity read"}
            >
              <CheckCheck size={14} /> {hasProjectAttention ? "Clear attention" : "Mark read"}
            </button>
          )}
        </div>
      </div>

      {!readOnlyHistorical && !canAddActivity && (
        <div className="mb-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
          V1 activity logging is limited to Graphics. Sales and OSR users can review timeline history here.
        </div>
      )}

      {canAddActivity && composerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-xl rounded-xl border border-border bg-card p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Add Note</h3>
                <p className="text-xs text-muted-foreground">Add a note to the timeline and notify the right people when needed.</p>
              </div>
              <button
                type="button"
                onClick={() => setComposerOpen(false)}
                disabled={sending}
                className="rounded-lg bg-secondary p-2 hover:bg-secondary/80 disabled:opacity-50"
                aria-label="Close activity dialog"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-2">
                <select
                  value={activityType}
                  onChange={(event) => setActivityType(event.target.value)}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm"
                >
                  {ACTIVITY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={5}
                  autoFocus
                  placeholder="Add a project note, question, blocker, or customer update..."
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <label className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3 py-2">
                  <input type="checkbox" checked={notifySales} onChange={(event) => setNotifySales(event.target.checked)} />
                  Notify Sales
                </label>
                <label className="inline-flex items-center gap-2 rounded-lg bg-secondary px-3 py-2">
                  <input type="checkbox" checked={notifyGraphics} onChange={(event) => setNotifyGraphics(event.target.checked)} />
                  Notify Graphics
                </label>
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setComposerOpen(false)}
                  disabled={sending}
                  className="inline-flex items-center justify-center rounded-lg bg-secondary px-4 py-2.5 text-sm font-medium hover:bg-secondary/80 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addActivity}
                  disabled={sending || !message.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  <Send size={15} /> {sending ? "Adding..." : "Add Activity"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasProjectAttention && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          <Bell size={14} /> Queue attention is active for this project. Clear it after the activity has been handled.
        </div>
      )}

      {unreadCount > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          <Bell size={14} /> {unreadCount} unread project activit{unreadCount === 1 ? "y" : "ies"}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <button
          type="button"
          onClick={() => setActivityView("all")}
          className={`rounded-full border px-3 py-1 ${activityView === "all" ? "border-primary bg-primary/20 text-primary" : "border-border bg-secondary text-muted-foreground"}`}
        >
          All ({activityItems.length})
        </button>
        <button
          type="button"
          onClick={() => setActivityView("emails")}
          className={`rounded-full border px-3 py-1 ${activityView === "emails" ? "border-cyan-500 bg-cyan-500/20 text-cyan-100" : "border-border bg-secondary text-muted-foreground"}`}
        >
          Emails ({emailActivities.length})
        </button>
        <button
          type="button"
          onClick={() => setActivityView("system")}
          className={`rounded-full border px-3 py-1 ${activityView === "system" ? "border-primary bg-primary/20 text-primary" : "border-border bg-secondary text-muted-foreground"}`}
        >
          System ({nonEmailActivities.length})
        </button>
      </div>

      <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
        {visibleActivityItems.map(renderActivity)}
        {visibleActivityItems.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {activityView === "emails" ? "No imported email activity recorded for this project." : "No activity recorded."}
          </p>
        )}
      </div>
    </section>
  );
};
